import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Calendar, MapPin, Music, Trophy, Theater, Users, Sparkles, Loader2, Navigation, MapPinOff } from 'lucide-react';
import searchCitiesAPI, { detectUserCity } from '../utils/citySearchService';

// Event category filters with background images
const EVENT_CATEGORIES = [
  {
    id: 'all',
    name: 'Tous',
    icon: Sparkles,
    segments: [],
    bgImage: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=400&q=80',
    gradient: 'from-purple-600/80 to-pink-600/80'
  },
  {
    id: 'music',
    name: 'Musique',
    icon: Music,
    segments: ['Music'],
    bgImage: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400&q=80',
    gradient: 'from-brand-600/80 to-cyan-600/80'
  },
  {
    id: 'sports',
    name: 'Sports',
    icon: Trophy,
    segments: ['Sports'],
    bgImage: 'https://images.unsplash.com/photo-1461896836934-28e4ba95d8f2?w=400&q=80',
    gradient: 'from-orange-600/80 to-red-600/80'
  },
  {
    id: 'arts',
    name: 'Arts & Théâtre',
    icon: Theater,
    segments: ['Arts & Theatre'],
    bgImage: 'https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?w=400&q=80',
    gradient: 'from-indigo-600/80 to-purple-600/80'
  },
  {
    id: 'family',
    name: 'Famille',
    icon: Users,
    segments: ['Miscellaneous', 'Family'],
    bgImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80',
    gradient: 'from-green-600/80 to-emerald-600/80'
  }
];

// Function to detect generic images and replace with category-specific ones
const getImageForEvent = (event) => {
  // List of generic/placeholder image patterns to detect
  // Including the Ticketmaster generic image ID
  const genericPatterns = [
    'placeholder',
    'default',
    'generic',
    'no-image',
    'coming-soon',
    'event-placeholder',
    '8cf/a6653880-7899-4f67-8067-1f95f4d158cf',  // Ticketmaster generic image
    '124761_TABLET_LANDSCAPE'  // Another Ticketmaster generic pattern
  ];
  
  // Check if the event has a generic image or no image
  const hasGenericImage = !event.image || 
    genericPatterns.some(pattern => 
      event.image.toLowerCase().includes(pattern.toLowerCase())
    );
  
  if (hasGenericImage) {
    // Find appropriate category based on event segment
    const eventSegment = event.segment?.toLowerCase() || '';
    
    if (eventSegment.includes('music')) {
      return EVENT_CATEGORIES.find(c => c.id === 'music')?.bgImage;
    } else if (eventSegment.includes('sport')) {
      return EVENT_CATEGORIES.find(c => c.id === 'sports')?.bgImage;
    } else if (eventSegment.includes('arts') || eventSegment.includes('theatre') || eventSegment.includes('theater')) {
      return EVENT_CATEGORIES.find(c => c.id === 'arts')?.bgImage;
    } else if (eventSegment.includes('family') || eventSegment.includes('miscellaneous')) {
      return EVENT_CATEGORIES.find(c => c.id === 'family')?.bgImage;
    } else {
      // Default to 'all' category image for unknown segments
      return EVENT_CATEGORIES.find(c => c.id === 'all')?.bgImage;
    }
  }
  
  return event.image;
};

const UpcomingEventsWidget = ({ 
  defaultCountryCode = "CA",
  autoLoad = false 
}) => {
  const navigate = useNavigate();
  const [city, setCity] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [searchingCities, setSearchingCities] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const searchTimeoutRef = useRef(null);

  // Auto-detect user location on component mount
  const handleDetectLocation = useCallback(async () => {
    setDetectingLocation(true);
    setLocationError(null);
    
    try {
      const detectedCity = await detectUserCity();
      
      if (detectedCity) {
        setCity(detectedCity.city);
        setCityInput(detectedCity.fullName);
        setCountryCode(detectedCity.country);
        setShowCitySuggestions(false);
      } else {
        setLocationError("Impossible de détecter votre ville");
      }
    } catch (err) {
      setLocationError(err.message || "Erreur de géolocalisation");
    } finally {
      setDetectingLocation(false);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (!city || !city.trim()) {
        throw new Error('Veuillez entrer une ville');
      }
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/events/upcoming?size=200&city=${encodeURIComponent(city)}&countryCode=${countryCode}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to fetch events');
      }
      
      setEvents(data.events || []);
    } catch (err) {
      setError(err.message);
      console.error('Error loading events:', err);
    } finally {
      setLoading(false);
    }
  }, [city, countryCode]);

  useEffect(() => {
    if (autoLoad && city) {
      loadEvents();
    }
  }, [autoLoad, loadEvents, city]);
  
  // Handle city input changes and show suggestions (async with debounce)
  const handleCityInputChange = (value) => {
    setCityInput(value);
    
    // Clear city when user starts typing (will be set when they select a suggestion or press search)
    setCity("");
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (value.trim().length >= 2) {
      setSearchingCities(true);
      
      // Debounce API calls by 300ms
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const suggestions = await searchCitiesAPI(value, "CA,US", 15);
          setCitySuggestions(suggestions);
          setShowCitySuggestions(suggestions.length > 0);
        } catch (error) {
          console.error('City search error:', error);
          setCitySuggestions([]);
          setShowCitySuggestions(false);
        } finally {
          setSearchingCities(false);
        }
      }, 300);
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
      setSearchingCities(false);
    }
  };
  
  // Handle city selection from suggestions
  const handleCitySelect = (cityData) => {
    setCity(cityData.city);
    setCityInput(cityData.fullName);
    setShowCitySuggestions(false);
    setCitySuggestions([]);
  };
  
  // Handle search button click
  const handleSearch = () => {
    // If user typed something but didn't select from suggestions, use the input as city
    if (cityInput && !city) {
      setCity(cityInput.split(',')[0].trim());
    }
    loadEvents();
  };
  
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowCitySuggestions(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Filter events based on active category
  const filteredEvents = events.filter(event => {
    if (activeCategory === 'all') return true;
    const category = EVENT_CATEGORIES.find(c => c.id === activeCategory);
    if (!category) return true;
    return category.segments.some(segment => 
      event.segment?.toLowerCase().includes(segment.toLowerCase())
    );
  });

  // Count events per category
  const getCategoryCount = (categoryId) => {
    if (categoryId === 'all') return events.length;
    const category = EVENT_CATEGORIES.find(c => c.id === categoryId);
    if (!category) return 0;
    return events.filter(event => 
      category.segments.some(segment => 
        event.segment?.toLowerCase().includes(segment.toLowerCase())
      )
    ).length;
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-8">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
        Événements à venir près de chez vous
      </h2>

      {/* Search Form */}
      <Card className="mb-8">
        <CardContent className="p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative md:col-span-2" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Ville
                </label>
                {/* Geolocation Button */}
                <button
                  type="button"
                  onClick={handleDetectLocation}
                  disabled={detectingLocation}
                  className="flex items-center gap-1.5 text-xs text-brand-600 hover:text-brand-700 transition-colors disabled:opacity-50"
                  data-testid="detect-location-btn"
                >
                  {detectingLocation ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Détection...</span>
                    </>
                  ) : (
                    <>
                      <Navigation className="h-3.5 w-3.5" />
                      <span>Utiliser ma position</span>
                    </>
                  )}
                </button>
              </div>
              
              {/* Location Error */}
              {locationError && (
                <div className="flex items-center gap-1.5 text-xs text-amber-600 mb-2">
                  <MapPinOff className="h-3.5 w-3.5" />
                  <span>{locationError}</span>
                </div>
              )}
              
              <div className="relative">
                <Input
                  type="text"
                  placeholder="ex: Montreal, Boucherville, L'Assomption..."
                  value={cityInput}
                  onChange={(e) => handleCityInputChange(e.target.value)}
                  onFocus={() => {
                    if (citySuggestions.length > 0) {
                      setShowCitySuggestions(true);
                    }
                  }}
                  className="w-full"
                  data-testid="city-input"
                />
                {searchingCities && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-brand-600" />
                  </div>
                )}
              </div>
              
              {/* City Suggestions Dropdown */}
              {showCitySuggestions && citySuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {citySuggestions.map((cityData, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleCitySelect(cityData)}
                      className="w-full text-left px-4 py-3 hover:bg-brand-50 hover:text-brand-700 transition-colors flex items-center justify-between border-b border-gray-100 last:border-b-0"
                      data-testid={`city-suggestion-${index}`}
                    >
                      <span className="font-medium">{cityData.fullName}</span>
                      <MapPin className="h-4 w-4 text-gray-400" />
                    </button>
                  ))}
                </div>
              )}
              
              {/* No results message */}
              {showCitySuggestions && citySuggestions.length === 0 && cityInput.length >= 2 && !searchingCities && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4 text-gray-500 text-sm">
                  Aucune ville trouvée pour "{cityInput}"
                </div>
              )}
            </div>
            
            <div className="flex items-end">
              <Button 
                onClick={handleSearch}
                disabled={loading || !cityInput}
                className="w-full bg-brand-600 hover:bg-brand-700 text-white"
                data-testid="search-events-btn"
              >
                {loading ? 'Recherche...' : 'Rechercher'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Filter Buttons */}
      {events.length > 0 && !loading && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Filtrer par catégorie</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {EVENT_CATEGORIES.map((category) => {
              const Icon = category.icon;
              const count = getCategoryCount(category.id);
              const isActive = activeCategory === category.id;
              
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  disabled={count === 0 && category.id !== 'all'}
                  className={`
                    relative overflow-hidden rounded-xl h-24 transition-all duration-300 transform
                    ${isActive 
                      ? 'ring-4 ring-brand-500 ring-offset-2 scale-105 shadow-xl' 
                      : 'hover:scale-102 hover:shadow-lg shadow-md'
                    }
                    ${count === 0 && category.id !== 'all' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  data-testid={`filter-${category.id}`}
                >
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${category.bgImage})` }}
                  />
                  
                  {/* Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${category.gradient}`} />
                  
                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute top-2 right-2">
                      <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="relative z-10 h-full flex flex-col items-center justify-center text-white p-2">
                    <Icon className={`w-8 h-8 mb-1 ${isActive ? 'animate-bounce' : ''}`} />
                    <span className="font-bold text-sm text-center">{category.name}</span>
                    <span className="text-xs opacity-90">({count})</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md mb-6">
          <p className="font-medium">Erreur: {error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
          <p className="mt-4 text-gray-600">Chargement des événements...</p>
        </div>
      )}

      {/* Initial State - No search yet */}
      {!loading && events.length === 0 && !error && !city && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <MapPin className="w-16 h-16 mx-auto text-brand-400 mb-4" />
          <p className="text-gray-600 text-lg">Entrez une ville pour voir les événements</p>
          <p className="text-gray-500 text-sm mt-2">Commencez à taper le nom d'une ville et sélectionnez-la dans les suggestions.</p>
        </div>
      )}

      {/* No Events Found */}
      {!loading && events.length === 0 && !error && city && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600 text-lg">Aucun événement trouvé dans cette ville.</p>
          <p className="text-gray-500 text-sm mt-2">Essayez une autre ville.</p>
        </div>
      )}

      {!loading && filteredEvents.length === 0 && events.length > 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600 text-lg">Aucun événement dans cette catégorie.</p>
          <p className="text-gray-500 text-sm mt-2">
            <button 
              onClick={() => setActiveCategory('all')}
              className="text-brand-600 hover:underline"
            >
              Voir tous les événements
            </button>
          </p>
        </div>
      )}

      {!loading && filteredEvents.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredEvents.map((event) => (
              <Card
                key={event.id}
                onClick={() => event.id && navigate(`/event/${event.id}`)}
                className="overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer hover:-translate-y-0.5 hover:border-brand-300"
                data-testid={`event-card-${event.id}`}
              >
                {(event.image || getImageForEvent(event)) && (
                  <div className="relative h-48 w-full overflow-hidden bg-gray-200">
                    <img
                      src={getImageForEvent(event)}
                      alt={event.name}
                      className="w-full h-full object-cover"
                    />
                    {/* Category badge */}
                    {event.segment && (
                      <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                        {event.segment}
                      </div>
                    )}
                  </div>
                )}
                
                <CardContent className="p-4">
                  <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">
                    {event.name}
                  </h3>
                  
                  {event.date && (
                    <div className="flex items-center text-sm text-gray-600 mb-1">
                      <Calendar className="w-4 h-4 mr-2 text-brand-600" />
                      <span>
                        {event.date}
                        {event.time && ` • ${event.time}`}
                      </span>
                    </div>
                  )}
                  
                  {(event.venue || event.city) && (
                    <div className="flex items-center text-sm text-gray-600 mb-3">
                      <MapPin className="w-4 h-4 mr-2 text-brand-600" />
                      <span className="line-clamp-1">
                        {event.venue && event.city 
                          ? `${event.venue}, ${event.city}`
                          : event.venue || event.city}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Results Count */}
          <div className="mt-8 text-center">
            <p className="text-gray-600">
              {activeCategory === 'all' 
                ? `${filteredEvents.length} événement${filteredEvents.length !== 1 ? 's' : ''} trouvé${filteredEvents.length !== 1 ? 's' : ''}`
                : `${filteredEvents.length} événement${filteredEvents.length !== 1 ? 's' : ''} dans cette catégorie sur ${events.length} au total`
              }
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default UpcomingEventsWidget;
